import type { Account, Tier } from '@/modules/account/types';
import { RootStore } from '@/store';
import { apiRequest, axiosInstance } from '@/utils';
import { helika } from '@/utils/helika';
import { storage } from '@/utils/storage';
import { makeAutoObservable } from 'mobx';

import * as Sentry from '@sentry/react';
import ym from 'react-yandex-metrika';

export class AccountStore {
  readonly rootStore: RootStore;
  public accountData: Account = {} as Account;
  public nextTier: Tier = {} as Tier;
  public friends: any[] = [];
  public isSettingsOpen: boolean = false;
  public authorizationToken: string | null = null;

  constructor({ rootStore }: { rootStore: RootStore }) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  public async setAuthorizationToken(token: string | null) {
    if (token) await storage.setItem('token', token);
    else await storage.removeItem('token');

    axiosInstance.defaults.headers.common['Authorization'] = token;
    this.authorizationToken = token;
  }

  private setUserIdToGTM(userId: string) {
    if (!userId) return;
    const windowAny = window as any;
    windowAny.dataLayer = windowAny.dataLayer || [];
    if (
      !windowAny.dataLayer.some(
        ({ event }: { event: string }) => event === 'userDataReady',
      )
    ) {
      windowAny.dataLayer.push({
        userId,
        event: 'userDataReady',
      });
    }
  }

  public updateAccount = async (payload: Record<string, any>) => {
    const [res] = await apiRequest([
      {
        path: '/v1/account/update',
        method: 'PATCH',
        body: payload,
      },
    ]);
    await this.getAccountData();
    return res;
  };

  public updateVoteBalance(value: string) {
    this.accountData.tokens = Number(value);
  }

  private setAccountData = (accountData: Account) => {
    this.accountData = accountData;
  };

  private setHelikaDetails(accountData: Account) {
    helika.helikaSDK?.setUserDetails({
      user_id: accountData._id,
      first_name: accountData.username,
      telegram_id: accountData.id,
      google_id: accountData.google_id,
      invited_by: accountData.invited_by,
    });
  }

  public setYandexMetrikaParams(accountData: Account) {
    ym('userParams', {
      user_id: accountData._id,
      first_name: accountData.username,
      telegram_id: accountData.id,
      google_id: accountData.google_id,
      invited_by: accountData.invited_by,
    });
  }

  private setSentryUser(accountData: Account) {
    Sentry.setUser({
      id: accountData._id,
      username: accountData.username,
    });
  }

  public getAccountData = async () => {
    const token = (await storage.getItem('token')) || '';
    const [accountData] = await apiRequest([
      {
        path: '/v1/account/data',
        method: 'GET',
        headers: {
          Authorization: token,
        },
      },
    ]);

    if (accountData?._id) {
      this.setAccountData(accountData);
      this.setUserIdToGTM(accountData._id);
      this.setHelikaDetails(accountData);
      if (import.meta.env.MODE !== 'development') {
        this.setSentryUser(accountData);
      }
    }

    return accountData;
  };

  get inviteCode() {
    return this.accountData.invite_code || null;
  }

  private setNextTier = (tier: Tier) => {
    this.nextTier = tier;
  };

  public getNextTier = async () => {
    const [nextTier] = await apiRequest(
      [
        {
          path: '/v1/account/tier/next',
          method: 'GET',
        },
      ],
      this.rootStore.errorStore,
    );

    this.setNextTier(nextTier);
  };

  private setFriends = (friends: []) => {
    this.friends = friends;
  };

  public getFriends = async () => {
    const [friends, inviter] = await apiRequest(
      [
        {
          path: '/v1/account/friends?sort=tokens.desc',
          method: 'GET',
        },
        {
          path: '/v1/referral/inviter',
          method: 'GET',
        },
      ],
      this.rootStore.errorStore,
    );
    let newFriends = friends;
    if (inviter) {
      newFriends = [{ ...inviter, inviter: true }, ...(newFriends || [])];
    }

    this.setFriends(newFriends);
  };

  public toggleOpenSettings = () => {
    this.isSettingsOpen = !this.isSettingsOpen;
  };

  public clickEgg = async (clicks?: number) => {
    const [result] = await apiRequest(
      [
        {
          path: '/v1/egg/click',
          method: 'POST',
          body: {
            count: clicks ?? 1,
          },
        },
      ],
      this.rootStore.errorStore,
    );
    return result;
  };
}
